import { Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { LanguageCell } from "../components/LanguageCell";
import { StatusCell } from "../components/StatusCell";
import { OriginalFileCell } from "../components/OriginalFileCell";
import { TranslatedFileCell } from "../components/TranslatedFileCell";
import { FinalFileCell } from "../components/FinalFileCell";
import { TranslatorCell } from "../components/TranslatorCell";
import { ManagerCell } from "../components/ManagerCell";
import { ActionCell } from "../components/ActionCell";

type RowObj = {
  document_id: string;
  language: string;
  document_status_id: string;
  file: null;
  order_id: string;
  origin_file?: any;
  lastest_file?: any;
  translators: any[];
  managers: any[];
  actionCol: string;
  hasChanged: boolean;
  new_file: any;
  source_lang?: string;
  target_lang?: string;
  translated_file?: string;
  final_file_cot?: any;
};

const columnHelper = createColumnHelper<RowObj>();

export const createTableColumns = (
  availableTargetLanguages: string[],
  translators: any[],
  managers: any[],
  tableData: any[],
  handlers: {
    onChangeDocumentStatus: (documentId: string, option: any) => void;
    onAssignTranslator: (documentId: string, translator: any) => void;
    onAssignManager: (documentId: string, manager: any) => void;
    onClickSaveDocument: (documentId: string) => void;
    onDownloadFiles: (documentId: string, fileName: string) => void;
    handleChange: (documentId: string, files: File[]) => void;
    onClickView: (user: any) => void;
    onTargetLanguageChange: (documentId: string, value: string) => void;
  }
) => [
  columnHelper.accessor("language", {
    id: "language",
    header: () => (
      <Text
        justifyContent="space-between"
        align="center"
        fontSize={{ sm: "10px", lg: "12px" }}
        lineHeight="24px"
        fontWeight="400"
        color="gray.500"
      >
        Language
      </Text>
    ),
    cell: (info) => (
      <LanguageCell
        sourceLanguage={info.row.original?.source_lang}
        targetLanguage={info.row.original?.target_lang}
        availableTargetLanguages={availableTargetLanguages}
        onTargetLanguageChange={(nextValue) =>
          handlers.onTargetLanguageChange(info.row.original?.document_id, nextValue)
        }
      />
    ),
  }),
  columnHelper.accessor("document_status_id", {
    id: "document_status_id",
    header: () => (
      <Text
        justifyContent="space-between"
        align="center"
        fontSize={{ sm: "10px", lg: "12px" }}
        lineHeight="24px"
        fontWeight="400"
        color="gray.500"
      >
        Status
      </Text>
    ),
    cell: (info) => (
      <StatusCell
        value={info.getValue()}
        onChangeDocumentStatus={handlers.onChangeDocumentStatus}
        documentId={info.row.original.document_id}
      />
    ),
  }),
  columnHelper.accessor("origin_file", {
    id: "origin_file",
    header: () => (
      <Text
        justifyContent="space-between"
        align="center"
        fontSize={{ sm: "10px", lg: "12px" }}
        lineHeight="24px"
        fontWeight="400"
        color="gray.500"
      >
        Original File
      </Text>
    ),
    cell: (info) => <OriginalFileCell originFile={info.row.original.origin_file} />,
  }),
  columnHelper.accessor("translated_file", {
    id: "translated_file",
    header: () => (
      <Text
        justifyContent="space-between"
        align="center"
        fontSize={{ sm: "10px", lg: "12px" }}
        lineHeight="24px"
        fontWeight="400"
        color="gray.500"
      >
        Translated File
      </Text>
    ),
    cell: (info) => (
      <TranslatedFileCell
        documentStatusId={info.row.original?.document_status_id}
        newFile={info.row.original.new_file}
        latestFile={info.row.original.lastest_file}
        onDownloadFiles={handlers.onDownloadFiles}
        documentId={info.row.original.document_id}
        orderId={info.row.original.order_id}
      />
    ),
  }),
  columnHelper.accessor("final_file_cot", {
    id: "final_file_cot",
    header: () => (
      <Text
        justifyContent="space-between"
        align="center"
        fontSize={{ sm: "10px", lg: "12px" }}
        lineHeight="24px"
        fontWeight="400"
        color="gray.500"
      >
        Final File with COT
      </Text>
    ),
    cell: (info) => (
      <FinalFileCell
        documentStatusId={info.row.original?.document_status_id}
        handleChange={handlers.handleChange}
        documentId={info.row.original.document_id}
      />
    ),
  }),
  columnHelper.accessor("translators", {
    id: "translators",
    header: () => (
      <Text
        justifyContent="space-between"
        align="center"
        fontSize={{ sm: "10px", lg: "12px" }}
        lineHeight="24px"
        color="gray.500"
        fontWeight="400"
      >
        Translator
      </Text>
    ),
    cell: (info) => {
      const { source_lang, target_lang } = tableData[info?.row?.index || 0] || {
        source_lang: "",
        target_lang: "",
      };
      return (
        <TranslatorCell
          translators={info.getValue()}
          allTranslators={translators}
          sourceLanguage={source_lang}
          targetLanguage={target_lang}
          onAssignTranslator={handlers.onAssignTranslator}
          onClickView={handlers.onClickView}
          documentId={info.row.original.document_id}
        />
      );
    },
  }),
  columnHelper.accessor("managers", {
    id: "managers",
    header: () => (
      <Text
        justifyContent="space-between"
        align="center"
        fontSize={{ sm: "10px", lg: "12px" }}
        lineHeight="24px"
        fontWeight="400"
        color="gray.500"
      >
        Manager
      </Text>
    ),
    cell: (info) => (
      <ManagerCell
        managers={info.getValue()}
        allManagers={managers}
        onAssignManager={handlers.onAssignManager}
        onClickView={handlers.onClickView}
        documentId={info.row.original.document_id}
      />
    ),
  }),
  columnHelper.accessor("actionCol", {
    id: "actionCol",
    header: () => (
      <Text
        justifyContent="space-between"
        align="center"
        fontSize={{ sm: "10px", lg: "12px" }}
        lineHeight="24px"
        color="gray.500"
        fontWeight="400"
      ></Text>
    ),
    cell: (info) => (
      <ActionCell
        hasChanged={info.row.original.hasChanged}
        onClickSaveDocument={handlers.onClickSaveDocument}
        documentId={info.row.original.document_id}
      />
    ),
  }),
];
